import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
// import uniqWith from 'lodash.uniqwith';
import { nanoid } from 'nanoid';
import deepFreeze from 'deep-freeze';
// import { diff } from 'deep-object-diff';

import api from '@/api/plantsCare';
import { mutationTypes as geobaseMutationTypes } from './glossaryGeobase';
import { mutationTypes as clientsMutaionTypes } from './clientsList';
import { mutationTypes as plantsCareMutationTypes } from './plantsCare';

const getEffectiveQuantity = (product) =>
  product?.current_quantity ?? product?.quantity ?? 0;

const getSelectionScopeConfig = (forStimulationType) => ({
  objectKey: forStimulationType ? 'type_stimulation_id' : 'pest_id',
  productsListKey: forStimulationType
    ? 'programTypesStimulationProducts'
    : 'programPestsProducts',
  combinationsListKey: forStimulationType
    ? 'programTypesStimulationCombinations'
    : 'programPestsCombinations',
  combinationProductsListKey: forStimulationType
    ? 'programTypesStimulationCombinationsProducts'
    : 'programPestsCombinationsProducts',
});

const getSelectedProductIdsInScope = (
  state,
  {
    program_phase_id,
    object_id,
    forStimulationType = false,
    excludeComboId = null,
  }
) => {
  const {
    objectKey,
    productsListKey,
    combinationsListKey,
    combinationProductsListKey,
  } = getSelectionScopeConfig(forStimulationType);

  const selectedIds = new Set(
    state[productsListKey]
      .filter(
        (product) =>
          product.program_phase_id === program_phase_id &&
          product[objectKey] === object_id &&
          product.is_selected
      )
      .map((product) => product.product_id)
  );

  const selectedComboIds = new Set(
    state[combinationsListKey]
      .filter(
        (combo) =>
          combo.program_phase_id === program_phase_id &&
          combo[objectKey] === object_id &&
          combo.is_selected &&
          combo.id !== excludeComboId
      )
      .map((combo) => combo.id)
  );

  state[combinationProductsListKey]
    .filter((product) => selectedComboIds.has(product.combination_id))
    .forEach((product) => selectedIds.add(product.product_id));

  return selectedIds;
};

const isProductSelectionBlockedInScope = (
  state,
  { product_id, program_phase_id, object_id, forStimulationType = false }
) =>
  getSelectedProductIdsInScope(state, {
    program_phase_id,
    object_id,
    forStimulationType,
  }).has(product_id);

const isCombinationSelectionBlockedInScope = (
  state,
  { id, program_phase_id, object_id, forStimulationType = false }
) => {
  const { combinationsListKey, combinationProductsListKey } =
    getSelectionScopeConfig(forStimulationType);

  const combo = state[combinationsListKey].find((item) => item.id === id);
  if (!combo) return false;

  const selectedProductIds = getSelectedProductIdsInScope(state, {
    program_phase_id,
    object_id,
    forStimulationType,
    excludeComboId: id,
  });

  return state[combinationProductsListKey]
    .filter((product) => product.combination_id === combo.id)
    .some((product) => selectedProductIds.has(product.product_id));
};

const getProductSelectionConflictsInScope = (
  state,
  { product_id, program_phase_id, object_id, forStimulationType = false }
) => {
  const {
    objectKey,
    productsListKey,
    combinationsListKey,
    combinationProductsListKey,
  } = getSelectionScopeConfig(forStimulationType);

  const conflictingProducts = state[productsListKey]
    .filter(
      (product) =>
        product.program_phase_id === program_phase_id &&
        product[objectKey] === object_id &&
        product.product_id === product_id &&
        product.is_selected
    )
    .map((product) => product.product_id);

  const conflictingCombinationIds = state[combinationsListKey]
    .filter(
      (combo) =>
        combo.program_phase_id === program_phase_id &&
        combo[objectKey] === object_id &&
        combo.is_selected
    )
    .filter((combo) =>
      state[combinationProductsListKey].some(
        (product) =>
          product.combination_id === combo.id &&
          product.product_id === product_id
      )
    )
    .map((combo) => combo.id);

  const conflictingComboProductIds = state[combinationProductsListKey]
    .filter((product) =>
      conflictingCombinationIds.includes(product.combination_id)
    )
    .map((product) => product.product_id);

  return {
    productIds: [...new Set(conflictingProducts)],
    combinationIds: conflictingCombinationIds,
    conflictedProductIds: [
      ...new Set([...conflictingProducts, ...conflictingComboProductIds]),
    ],
  };
};

const getCombinationSelectionConflictsInScope = (
  state,
  { id, program_phase_id, object_id, forStimulationType = false }
) => {
  const {
    objectKey,
    productsListKey,
    combinationsListKey,
    combinationProductsListKey,
  } = getSelectionScopeConfig(forStimulationType);

  const targetProductIds = new Set(
    state[combinationProductsListKey]
      .filter((product) => product.combination_id === id)
      .map((product) => product.product_id)
  );

  const conflictingProducts = state[productsListKey]
    .filter(
      (product) =>
        product.program_phase_id === program_phase_id &&
        product[objectKey] === object_id &&
        product.is_selected &&
        targetProductIds.has(product.product_id)
    )
    .map((product) => product.product_id);

  const conflictingCombinationIds = state[combinationsListKey]
    .filter(
      (combo) =>
        combo.program_phase_id === program_phase_id &&
        combo[objectKey] === object_id &&
        combo.is_selected &&
        combo.id !== id
    )
    .filter((combo) =>
      state[combinationProductsListKey].some(
        (product) =>
          product.combination_id === combo.id &&
          targetProductIds.has(product.product_id)
      )
    )
    .map((combo) => combo.id);

  const conflictingComboProductIds = state[combinationProductsListKey]
    .filter((product) =>
      conflictingCombinationIds.includes(product.combination_id)
    )
    .map((product) => product.product_id);

  return {
    productIds: [...new Set(conflictingProducts)],
    combinationIds: conflictingCombinationIds,
    conflictedProductIds: [
      ...new Set([...conflictingProducts, ...conflictingComboProductIds]),
    ],
  };
};

const serverKeysProgram = [
  'categoriesCultures',
  'cultures',
  'culturesSorts',
  'images',
  'manufacturers',
  'manufacturersCountries',
  'pests',
  'typesStimulation',
  'phases',
  'phasesTypesStimulation',
  'phasesPests',
  'productsPests',
  'productsTypesStimulation',
  'programPhasesPests',
  'programPhasesTypesStimulation',
  'programPestsCombinations',
  'programPestsCombinationsProducts',
  'programTypesStimulationCombinations',
  'programTypesStimulationCombinationsProducts',
];

const serverKeysClient = [
  'clientPlaces',
  'clientCultures',
  'clientPlacesZones',
];

const editableKeysProgram = [
  'programCulturesSorts',
  'programPhases',
  'programPestsProducts',
  'programTypesStimulationProducts',
  'programPestsCombinations',
  'programPestsCombinationsProducts',
  'programTypesStimulationCombinations',
  'programTypesStimulationCombinationsProducts',
];

const defaultProgram = {
  name: '',
  description: '',
  client_id: null,
  client_name: null,
  client_type: null,
  client_discount: 0,
  client_code: null,
  geo_id: null,
  zone_name: '',
  place_area: null,
  culture_id: null,
  culture_age: null,
  calculation_method_id: null,
  template_id: null,
  template_name: '',
  rate_dollar: null,
  rate_euro: null,
};

const createState = () => ({
  isLoading: false,
  isSaving: false,
  isClientFetching: false,
  clientFetchAbortController: null,
  templatesSearching: false,
  templatesSearchAbortController: null,
  templateFetching: false,
  templateFetchAbortController: null,
  error: null,
  timestamp: 0,
  serverData: {
    program: { ...defaultProgram },
    ...Object.fromEntries(editableKeysProgram.map((key) => [key, []])),
  },
  program: { ...defaultProgram },
  ...Object.fromEntries(
    [...serverKeysProgram, ...editableKeysProgram].map((key) => [key, []])
  ),
  client: null,
  ...Object.fromEntries(serverKeysClient.map((key) => [key, []])),
  templates: [],
  selectedTemplate: null,
  savedRedirectId: null,
  lastInsertedPhaseId: null,
});

const state = createState();

const mutationTypes = {
  resetState: '[plants care p] reset state',

  programFetchingStart: '[plants care p] program fetching start',
  programFetchingFailure: '[plants care p] program fetching failure',
  programFetchingSuccess: '[plants care p] program fetching success',

  setClientCode: '[plants care p] set program code',
  setProgramName: '[plants care p] set program name',
  setProgramDescription: '[plants care p] set program description',
  setClientName: '[plants care p] set client name',
  setClientType: '[plants care p] set client type',
  setClientId: '[plants care p] set client id',
  setClientDiscount: '[plants care p] set client discount',
  setProgramGeo: '[plants care p] set program geo',
  setZoneName: '[plants care p] set zone name',
  setPlaceArea: '[plants care p] set place area',
  setCultureId: '[plants care p] set culture id',
  setAge: '[plants care p] set age',
  setCalculationMethod: '[plants care p] set calculation method',
  setCultureSorts: '[plants care p] set culture sorts',
  setRateEU: '[plants care p] set EU exchange rate',
  setRateUSD: '[plants care p] set USD exchange rate',
  deleteId: '[plants care p] delete id',
  clearInnactiveProducts: '[plants care p] clear innactive products',

  clientFetchingStart: '[plants care p] client fetching start',
  clientFetchingFailure: '[plants care p] client fetching failure',
  clientFetchingSuccess: '[plants care p] client fetching success',
  clientFetchingSetAbortController:
    '[plants care p] client fetching set abort controller',
  resetClient: '[plants care p] reset client',

  templatesSearchStart: '[plants care p] tempaltes search start',
  templatesSearchFailure: '[plants care p] tempaltes search failure',
  templatesSearchSuccess: '[plants care p] tempaltes search success',
  templatesSearchSetAbortController:
    '[plants care p]  tempaltes search set abort controller',
  selectTemplate: '[plants care p] select template',

  templateFetchingStart: '[plants care p] template fetching start',
  templateFetchingFailure: '[plants care p] template fetching failure',
  templateFetchingSuccess: '[plants care p] template fetching success',

  setSelectedProduct: '[plants care p] set selected product',

  addTemporaryPhase: '[plants care p] add temporary phase',
  removeTemporaryPhase: '[plants care p] remove temporary phase',
  addPest: '[plants care p] add pest',
  addStimulationType: '[plants care p] add stimulation type',
  removePest: '[plants care p] remove pest',
  removeStimulationType: '[plants care p] remove stimulation type',

  setComment: '[plants care p] set comment',
  setWaterPhase: '[plants care p] set water phase',
  setPestProductQuantity: '[plants care p] set pest product quantity',
  setStimulationTypeProductQuantity:
    '[plants care p] set stimulation type product quantity',

  addPestCombination: '[plants care p] add pest combination',
  removePestCombination: '[plants care p] remove pest combination',
  updatePestCombinationComment:
    '[plants care p] update pest combination comment',
  addPestCombinationProduct: '[plants care p] add pest combination product',
  removePestCombinationProduct:
    '[plants care p] remove pest combination product',
  setPestCombinationProductQuantity:
    '[plants care p] set pest combination product quantity',
  setSelectedCombination: '[plants care p] set selected combination',

  addStimulationCombination: '[plants care p] add stimulation combination',
  removeStimulationCombination:
    '[plants care p] remove stimulation combination',
  updateStimulationCombinationComment:
    '[plants care p] update stimulation combination comment',
  addStimulationCombinationProduct:
    '[plants care p] add stimulation combination product',
  removeStimulationCombinationProduct:
    '[plants care p] remove stimulation combination product',
  setStimulationCombinationProductQuantity:
    '[plants care p] set stimulation combination product quantity',
  setSelectedStimulationCombination:
    '[plants care p] set selected stimulation combination',

  saveProgramStart: '[plants care p] save program start',
  saveProgramFailure: '[plants care p] save program failure',
  saveProgramSuccess: '[plants care p] save program success',
};

const actionTypes = {
  fetchProgram: '[plants care p] fetch program',
  fetchClient: '[plant care p] fetch client',
  templatesSearch: '[plants care p] tempaltes search',
  fetchTemplate: '[plants care p] fetch template',
  saveProgram: '[plants care p] save program',
  deleteProgram: '[plants care p] delete program',
};

const getters = {
  programPestsProductsAsMap(state) {
    return Object.fromEntries(
      state.programPestsProducts.map((p) => [
        `phase_${p.program_phase_id}_pest_${p.pest_id}_product_${p.product_id}`,
        p,
      ])
    );
  },
  programSTypesProductsAsMap(state) {
    return Object.fromEntries(
      state.programTypesStimulationProducts.map((p) => [
        `phase_${p.program_phase_id}_stype_${p.type_stimulation_id}_product_${p.product_id}`,
        p,
      ])
    );
  },
  isProgramProductSelectionBlocked: (state) => (payload) =>
    isProductSelectionBlockedInScope(state, payload),
  isProgramCombinationSelectionBlocked: (state) => (payload) =>
    isCombinationSelectionBlockedInScope(state, payload),
  getProgramProductSelectionConflicts: (state) => (payload) =>
    getProductSelectionConflictsInScope(state, payload),
  getProgramCombinationSelectionConflicts: (state) => (payload) =>
    getCombinationSelectionConflictsInScope(state, payload),
  programPricesMap(state, { plantsCareProductsAsMap }) {
    return Object.fromEntries(
      state.programPhases.map((phase) => [
        phase.id,
        [
          ...state.programPestsProducts.filter(
            (p) =>
              p.program_phase_id === phase.id &&
              (phase.is_temporary ? getEffectiveQuantity(p) > 0 : p.is_selected)
          ),
          ...state.programTypesStimulationProducts.filter(
            (p) =>
              p.program_phase_id === phase.id &&
              (phase.is_temporary ? getEffectiveQuantity(p) > 0 : p.is_selected)
          ),
          ...state.programPestsCombinationsProducts.filter((cp) => {
            const combo = state.programPestsCombinations.find(
              (c) => c.id === cp.combination_id
            );
            return phase.is_temporary
              ? combo?.program_phase_id === phase.id &&
                  getEffectiveQuantity(cp) > 0
              : combo?.is_selected && combo?.program_phase_id === phase.id;
          }),
          ...state.programTypesStimulationCombinationsProducts.filter((cp) => {
            const combo = state.programTypesStimulationCombinations.find(
              (c) => c.id === cp.combination_id
            );
            return phase.is_temporary
              ? combo?.program_phase_id === phase.id &&
                  getEffectiveQuantity(cp) > 0
              : combo?.is_selected && combo?.program_phase_id === phase.id;
          }),
        ].reduce((acc, cur) => {
          const product = plantsCareProductsAsMap[cur.product_id];
          const multiplier = product?.picking
            ? product.price / product.picking
            : 0;
          return acc + getEffectiveQuantity(cur) * multiplier;
        }, 0),
      ])
    );
  },
  programHasPestsOrSTypesWithoutProducts(state) {
    const hasEmptyPests = state.programPhasesPests.some(
      (pest) =>
        !state.programPestsProducts.some((p) => p.pest_id === pest.id) &&
        !state.programPestsCombinations.some((c) => c.pest_id === pest.id)
    );
    const hasEmptySTypes = state.programPhasesTypesStimulation.some(
      (sType) =>
        !state.programTypesStimulationProducts.some(
          (p) => p.type_stimulation_id === sType.id
        ) &&
        !state.programTypesStimulationCombinations.some(
          (c) => c.type_stimulation_id === sType.id
        )
    );
    return hasEmptyPests || hasEmptySTypes;
  },
  programsCanSave(state) {
    // console.log(
    //   diff(state.serverData, {
    //     program: state.program,
    //     ...Object.fromEntries(
    //       editableKeysProgram.map((key) => [key, state[key]])
    //     ),
    //   })
    // );
    return (
      state.program.name &&
      state.programPhases.length &&
      state.program.place_area &&
      !isEqual(state.serverData, {
        program: state.program,
        ...Object.fromEntries(
          editableKeysProgram.map((key) => [key, state[key]])
        ),
      })
    );
  },
};

const mutations = {
  [mutationTypes.resetState](state) {
    state.isLoading = false;
    state.error = null;
    state.program = { ...defaultProgram };
    editableKeysProgram.forEach((key) => (state[key] = []));
    state.selectedTemplate = null;
    state.templates = [];
    state.client = null;
  },
  [mutationTypes.programFetchingStart](state) {
    state.isLoading = true;
    state.error = null;
  },
  [mutationTypes.programFetchingFailure](state, payload) {
    state.isLoading = false;
    state.error = payload;
  },
  [mutationTypes.programFetchingSuccess](state, payload) {
    state.isLoading = false;
    state.error = null;
    state.timestamp = new Date().getTime();

    serverKeysProgram.forEach((key) => {
      if (payload[key]?.length) state[key] = payload[key];
    });

    ['program', ...editableKeysProgram].forEach((key) => {
      if (payload[key]) {
        state[key] = cloneDeep(payload[key]);
        state.serverData[key] = cloneDeep(payload[key]);
      }
    });
  },

  [mutationTypes.setProgramName](state, payload) {
    state.program.name = payload;
  },
  [mutationTypes.setProgramDescription](state, payload) {
    state.program.description = payload;
  },
  [mutationTypes.setClientCode](state, payload) {
    state.program.client_code = payload;
  },
  [mutationTypes.setClientName](state, payload) {
    state.program.client_name = payload;
  },
  [mutationTypes.setClientType](state, payload) {
    state.program.client_type = payload;
  },
  [mutationTypes.setClientId](state, payload) {
    state.program.client_id = payload;
  },
  [mutationTypes.setClientDiscount](state, payload) {
    state.program.client_discount = payload ?? 0;
  },
  [mutationTypes.setProgramGeo](state, payload) {
    state.program.geo_id = payload;
  },
  [mutationTypes.setZoneName](state, payload) {
    state.program.zone_name = payload;
  },
  [mutationTypes.setPlaceArea](state, payload) {
    state.program.place_area = payload;
  },
  [mutationTypes.setCultureId](state, payload) {
    state.program.culture_id = payload;
  },
  [mutationTypes.setAge]({ program }, payload) {
    program.culture_age = payload > 11 ? 11 : payload;
  },
  [mutationTypes.setCalculationMethod]({ program }, payload) {
    program.calculation_method_id = payload === null ? null : Number(payload);
  },
  [mutationTypes.setCultureSorts](state, payload) {
    state.programCulturesSorts = payload.map((sort_id) => ({ sort_id }));
  },
  [mutationTypes.setRateEU](state, payload) {
    state.program.rate_euro = payload;
  },
  [mutationTypes.setRateUSD](state, payload) {
    state.program.rate_dollar = payload;
  },
  [mutationTypes.deleteId](state) {
    delete state.program.id;
    delete state.program.client_id;
    delete state.program.client_discount;
  },
  [mutationTypes.clearInnactiveProducts](state, payload) {
    const ids = payload.map((p) => p.id);
    console.log(ids);

    const inactivePests = state.programPestsProducts.filter((p) =>
      ids.includes(p.product_id)
    );
    const inactiveSTypes = state.programTypesStimulationProducts.filter((p) =>
      ids.includes(p.product_id)
    );

    const oldInactivePests = [...(state.programPhasesPests ?? [])];
    const oldInactiveSTypes = [...(state.programPhasesTypesStimulation ?? [])];

    for (const pest of inactivePests) {
      const old = oldInactivePests.find(
        (p) =>
          p.pest_id === pest.pest_id &&
          p.program_phase_id === pest.program_phase_id
      );
      if (!old) {
        oldInactivePests.push(pest);
      }
    }

    for (const sType of inactiveSTypes) {
      const old = oldInactiveSTypes.find(
        (p) =>
          p.type_stimulation_id === sType.type_stimulation_id &&
          p.program_phase_id === sType.program_phase_id
      );
      if (!old) {
        oldInactiveSTypes.push(sType);
      }
    }

    state.programPhasesPests = oldInactivePests;
    state.programPhasesTypesStimulation = oldInactiveSTypes;

    state.programPestsProducts = state.programPestsProducts.filter(
      (p) => !ids.includes(p.product_id)
    );

    state.programTypesStimulationProducts =
      state.programTypesStimulationProducts.filter(
        (p) => !ids.includes(p.product_id)
      );

    state.programPestsCombinationsProducts =
      state.programPestsCombinationsProducts.filter(
        (p) => !ids.includes(p.product_id)
      );
    state.programTypesStimulationCombinationsProducts =
      state.programTypesStimulationCombinationsProducts.filter(
        (p) => !ids.includes(p.product_id)
      );

    const activePestComboIds = new Set(
      state.programPestsCombinationsProducts.map((p) => p.combination_id)
    );
    state.programPestsCombinations = state.programPestsCombinations.filter(
      (c) => activePestComboIds.has(c.id)
    );

    const activeStimComboIds = new Set(
      state.programTypesStimulationCombinationsProducts.map(
        (p) => p.combination_id
      )
    );
    state.programTypesStimulationCombinations =
      state.programTypesStimulationCombinations.filter((c) =>
        activeStimComboIds.has(c.id)
      );
  },

  [mutationTypes.clientFetchingStart](state) {
    state.isClientFetching = true;
    state.error = null;
  },
  [mutationTypes.clientFetchingFailure](state, payload) {
    if (payload?.message === 'canceled') return;
    state.error = payload;
    state.isClientFetching = false;
  },
  [mutationTypes.clientFetchingSuccess](state, payload) {
    state.error = null;
    state.isClientFetching = false;
    ['client', ...serverKeysClient].forEach(
      (key) => (state[key] = payload[key] ?? [])
    );
  },
  [mutationTypes.clientFetchingSetAbortController](state, payload) {
    state.clientFetchAbortController?.abort('canceled');
    state.clientFetchAbortController = payload;
  },
  [mutationTypes.resetClient](state) {
    state.client = null;
    serverKeysClient.forEach((key) => (state[key] = []));
  },

  [mutationTypes.templatesSearchStart](state) {
    state.templatesSearching = true;
    state.error = null;
  },
  [mutationTypes.templatesSearchFailure](state, payload) {
    if (payload?.message === 'canceled') return;
    state.templatesSearching = false;
    state.error = payload;
  },
  [mutationTypes.templatesSearchSuccess](state, payload) {
    state.templatesSearching = false;
    state.templates = payload;
    state.selectedTemplate = null;
    if (state.templates.length === 1) {
      state.selectedTemplate = state.templates[0];
      state.program.template_id = state.templates[0].id;
      state.program.template_name = state.templates[0].name ?? '';
    }
  },
  [mutationTypes.templatesSearchSetAbortController](state, payload) {
    state.templatesSearchAbortController?.abort('canceled');
    state.templatesSearchAbortController = payload;
  },
  [mutationTypes.selectTemplate](state, payload) {
    if (payload === null) {
      state.selectedTemplate = null;
      editableKeysProgram.forEach((key) => (state[key] = []));
      state.program.template_id = null;
      state.program.template_name = null;
      return;
    }
    const template = state.templates.find(({ id }) => id === payload);
    if (template) {
      state.selectedTemplate = template;
      state.program.template_id = template.id;
      state.program.template_name = template.name ?? '';
    }
  },

  [mutationTypes.templateFetchingStart](state) {
    state.templateFetching = true;
    state.error = null;
  },
  [mutationTypes.templateFetchingFailure](state, payload) {
    state.templateFetching = false;
    state.error = payload;
  },
  [mutationTypes.templateFetchingSuccess](state, payload) {
    serverKeysProgram.forEach(
      (key) => payload[key] && (state[key] = payload[key])
    );
    const phases = payload.phases
      .filter((p) => p.culture_id === state.program.culture_id)
      .map((p) => ({
        ...p,
        id: nanoid(),
        originalId: p.id,
        is_temporary: 0,
      }));

    state.programPestsProducts = [];
    state.programTypesStimulationProducts = [];
    state.programPestsCombinations = [];
    state.programPestsCombinationsProducts = [];
    state.programTypesStimulationCombinations = [];
    state.programTypesStimulationCombinationsProducts = [];
    for (const phase of phases) {
      const water = payload.templateWaterPhases.find(
        (wp) => wp.phase_id === phase.originalId
      )?.quantity;
      if (water) phase.volume_per_ha = water;

      phase.main_image =
        payload.images.find(
          (img) =>
            img.is_main && img.object_id === phase.originalId && img.type === 1
        )?.name ?? null;

      const pestsProducts = payload.templatePestsProducts
        .filter(({ phase_id }) => phase_id === phase.originalId)
        .map((pp) => ({
          id: nanoid(),
          program_phase_id: phase.id,
          pest_id: pp.pest_id,
          product_id: pp.product_id,
          quantity: pp.quantity,
          current_quantity: null,
          is_selected: 0,
        }));
      state.programPestsProducts = [
        ...state.programPestsProducts,
        ...pestsProducts,
      ];

      const tsProducts = payload.templateTypesStimulationProducts
        .filter(({ phase_id }) => phase_id === phase.originalId)
        .map((tsp) => ({
          id: nanoid(),
          program_phase_id: phase.id,
          type_stimulation_id: tsp.type_stimulation_id,
          product_id: tsp.product_id,
          quantity: tsp.quantity,
          current_quantity: null,
          is_selected: 0,
        }));
      state.programTypesStimulationProducts = [
        ...state.programTypesStimulationProducts,
        ...tsProducts,
      ];

      // Clone pest combinations
      const pestComboIdMap = {};
      (payload.templatePestsCombinations || [])
        .filter((c) => c.phase_id === phase.originalId)
        .forEach((c) => {
          const newId = nanoid();
          pestComboIdMap[c.id] = newId;
          state.programPestsCombinations.push({
            id: newId,
            program_phase_id: phase.id,
            pest_id: c.pest_id,
            comment: c.comment || '',
            is_selected: 0,
          });
        });
      (payload.templatePestsCombinationsProducts || [])
        .filter((p) => pestComboIdMap[p.combination_id])
        .forEach((p) => {
          state.programPestsCombinationsProducts.push({
            id: nanoid(),
            combination_id: pestComboIdMap[p.combination_id],
            product_id: p.product_id,
            quantity: p.quantity,
            current_quantity: null,
          });
        });

      // Clone stimulation combinations
      const stimComboIdMap = {};
      (payload.templateTypesStimulationCombinations || [])
        .filter((c) => c.phase_id === phase.originalId)
        .forEach((c) => {
          const newId = nanoid();
          stimComboIdMap[c.id] = newId;
          state.programTypesStimulationCombinations.push({
            id: newId,
            program_phase_id: phase.id,
            type_stimulation_id: c.type_stimulation_id,
            comment: c.comment || '',
            is_selected: 0,
          });
        });
      (payload.templateTypesStimulationCombinationsProducts || [])
        .filter((p) => stimComboIdMap[p.combination_id])
        .forEach((p) => {
          state.programTypesStimulationCombinationsProducts.push({
            id: nanoid(),
            combination_id: stimComboIdMap[p.combination_id],
            product_id: p.product_id,
            quantity: p.quantity,
            current_quantity: null,
          });
        });
    }

    state.programPhases = phases.filter(
      ({ id }) =>
        state.programPestsProducts.some((p) => p.program_phase_id === id) ||
        state.programTypesStimulationProducts.some(
          (p) => p.program_phase_id === id
        ) ||
        state.programPestsCombinations.some((c) => c.program_phase_id === id) ||
        state.programTypesStimulationCombinations.some(
          (c) => c.program_phase_id === id
        )
    );

    state.templateFetching = false;
  },

  [mutationTypes.setSelectedProduct](
    state,
    { object_id, product_id, program_phase_id, forStimulationType }
  ) {
    const list = forStimulationType
      ? state.programTypesStimulationProducts
      : state.programPestsProducts;

    const product = list.find(
      (p) =>
        p.product_id === product_id &&
        p.program_phase_id === program_phase_id &&
        object_id === (forStimulationType ? p.type_stimulation_id : p.pest_id)
    );
    if (!product) return;
    if (!product.is_selected) {
      const isBlocked = isProductSelectionBlockedInScope(state, {
        product_id,
        program_phase_id,
        object_id,
        forStimulationType,
      });
      if (isBlocked) return;
    }
    product.is_selected = product.is_selected ? 0 : 1;
  },

  [mutationTypes.addTemporaryPhase](state, payload) {
    let index = state.programPhases.findIndex(({ id }) => id === payload);
    const phase = {
      ...state.programPhases[index],
      id: nanoid(),
      is_temporary: 1,
      comment: '',
      volume_per_ha: null,
    };
    while (state.programPhases[index + 1]?.is_temporary === 1) {
      index += 1;
    }
    state.programPhases.splice(index + 1, 0, phase);
    state.lastInsertedPhaseId = phase.id;
  },
  [mutationTypes.removeTemporaryPhase](state, payload) {
    state.programPhases = state.programPhases.filter(
      ({ id }) => id !== payload
    );
    state.programPestsProducts = state.programPestsProducts.filter(
      ({ program_phase_id }) => program_phase_id !== payload
    );
    state.programTypesStimulationProducts =
      state.programTypesStimulationProducts.filter(
        ({ program_phase_id }) => program_phase_id !== payload
      );

    const removedPestComboIds = new Set(
      state.programPestsCombinations
        .filter((c) => c.program_phase_id === payload)
        .map((c) => c.id)
    );
    state.programPestsCombinations = state.programPestsCombinations.filter(
      (c) => c.program_phase_id !== payload
    );
    state.programPestsCombinationsProducts =
      state.programPestsCombinationsProducts.filter(
        (p) => !removedPestComboIds.has(p.combination_id)
      );

    const removedStimComboIds = new Set(
      state.programTypesStimulationCombinations
        .filter((c) => c.program_phase_id === payload)
        .map((c) => c.id)
    );
    state.programTypesStimulationCombinations =
      state.programTypesStimulationCombinations.filter(
        (c) => c.program_phase_id !== payload
      );
    state.programTypesStimulationCombinationsProducts =
      state.programTypesStimulationCombinationsProducts.filter(
        (p) => !removedStimComboIds.has(p.combination_id)
      );
  },
  [mutationTypes.addPest](state, { id, phaseId }) {
    state.productsPests
      .filter((e) => e.pest_id === id)
      .forEach(({ pest_id, product_id }) =>
        state.programPestsProducts.push({
          program_phase_id: phaseId,
          pest_id,
          product_id,
          quantity: null,
          current_quantity: null,
          is_selected: 1,
          // TODO: price
        })
      );
  },
  [mutationTypes.addStimulationType](state, { id, phaseId }) {
    state.productsTypesStimulation
      .filter((e) => e.type_stimulation_id === id)
      .forEach(({ type_stimulation_id, product_id }) =>
        state.programTypesStimulationProducts.push({
          program_phase_id: phaseId,
          type_stimulation_id,
          product_id,
          quantity: null,
          current_quantity: null,
          is_selected: 1,
          // TODO: price
        })
      );
  },
  [mutationTypes.removePest](state, { id, phaseId }) {
    state.programPestsProducts = state.programPestsProducts.filter(
      (p) => !(p.pest_id === id && p.program_phase_id === phaseId)
    );
    const removedComboIds = new Set(
      state.programPestsCombinations
        .filter((c) => c.pest_id === id && c.program_phase_id === phaseId)
        .map((c) => c.id)
    );
    state.programPestsCombinations = state.programPestsCombinations.filter(
      (c) => !(c.pest_id === id && c.program_phase_id === phaseId)
    );
    state.programPestsCombinationsProducts =
      state.programPestsCombinationsProducts.filter(
        (p) => !removedComboIds.has(p.combination_id)
      );
  },
  [mutationTypes.removeStimulationType](state, { id, phaseId }) {
    state.programTypesStimulationProducts =
      state.programTypesStimulationProducts.filter(
        (p) => !(p.type_stimulation_id === id && p.program_phase_id === phaseId)
      );
    const removedComboIds = new Set(
      state.programTypesStimulationCombinations
        .filter(
          (c) => c.type_stimulation_id === id && c.program_phase_id === phaseId
        )
        .map((c) => c.id)
    );
    state.programTypesStimulationCombinations =
      state.programTypesStimulationCombinations.filter(
        (c) => !(c.type_stimulation_id === id && c.program_phase_id === phaseId)
      );
    state.programTypesStimulationCombinationsProducts =
      state.programTypesStimulationCombinationsProducts.filter(
        (p) => !removedComboIds.has(p.combination_id)
      );
  },

  [mutationTypes.setComment](state, { id, comment }) {
    state.programPhases.find((p) => p.id === id).comment = comment;
  },
  [mutationTypes.setWaterPhase](state, { id, value }) {
    state.programPhases.find((p) => p.id === id).volume_per_ha = value;
  },
  [mutationTypes.setPestProductQuantity](state, payload) {
    const {
      product_id,
      pest_id,
      program_phase_id,
      quantity,
      current_quantity,
    } = payload;
    const product = state.programPestsProducts.find(
      (p) =>
        p.product_id === product_id &&
        p.pest_id === pest_id &&
        p.program_phase_id === program_phase_id
    );
    if (!product) return;
    if (Object.prototype.hasOwnProperty.call(payload, 'current_quantity')) {
      product.current_quantity = current_quantity;
      return;
    }
    product.quantity = quantity;
  },
  [mutationTypes.setStimulationTypeProductQuantity](state, payload) {
    const {
      product_id,
      type_stimulation_id,
      program_phase_id,
      quantity,
      current_quantity,
    } = payload;
    const product = state.programTypesStimulationProducts.find(
      (p) =>
        p.product_id === product_id &&
        p.type_stimulation_id === type_stimulation_id &&
        p.program_phase_id === program_phase_id
    );
    if (!product) return;
    if (Object.prototype.hasOwnProperty.call(payload, 'current_quantity')) {
      product.current_quantity = current_quantity;
      return;
    }
    product.quantity = quantity;
  },

  // Pest combination mutations
  [mutationTypes.addPestCombination](state, { program_phase_id, pest_id }) {
    state.programPestsCombinations.push({
      id: nanoid(),
      program_phase_id,
      pest_id,
      comment: '',
      is_selected: 0,
    });
  },
  [mutationTypes.removePestCombination](state, { id }) {
    state.programPestsCombinations = state.programPestsCombinations.filter(
      (c) => c.id !== id
    );
    state.programPestsCombinationsProducts =
      state.programPestsCombinationsProducts.filter(
        (p) => p.combination_id !== id
      );
  },
  [mutationTypes.updatePestCombinationComment](state, { id, comment }) {
    const combo = state.programPestsCombinations.find((c) => c.id === id);
    if (combo) combo.comment = comment;
  },
  [mutationTypes.addPestCombinationProduct](
    state,
    { combination_id, product_id }
  ) {
    state.programPestsCombinationsProducts.push({
      id: nanoid(),
      combination_id,
      product_id,
      quantity: null,
      current_quantity: null,
    });
  },
  [mutationTypes.removePestCombinationProduct](state, { id }) {
    state.programPestsCombinationsProducts =
      state.programPestsCombinationsProducts.filter((p) => p.id !== id);
  },
  [mutationTypes.setPestCombinationProductQuantity](state, payload) {
    const prod = state.programPestsCombinationsProducts.find(
      (p) => p.id === payload.id
    );
    if (!prod) return;
    if (Object.prototype.hasOwnProperty.call(payload, 'current_quantity')) {
      prod.current_quantity = payload.current_quantity;
      return;
    }
    prod.quantity = payload.quantity;
  },
  [mutationTypes.setSelectedCombination](state, { id, forStimulationType }) {
    const comboList = forStimulationType
      ? state.programTypesStimulationCombinations
      : state.programPestsCombinations;
    const combo = comboList.find((c) => c.id === id);
    if (!combo) return;
    if (!combo.is_selected) {
      const isBlocked = isCombinationSelectionBlockedInScope(state, {
        id,
        program_phase_id: combo.program_phase_id,
        object_id: forStimulationType
          ? combo.type_stimulation_id
          : combo.pest_id,
        forStimulationType,
      });
      if (isBlocked) return;
    }
    combo.is_selected = combo.is_selected ? 0 : 1;
  },

  // Stimulation combination mutations
  [mutationTypes.addStimulationCombination](
    state,
    { program_phase_id, type_stimulation_id }
  ) {
    state.programTypesStimulationCombinations.push({
      id: nanoid(),
      program_phase_id,
      type_stimulation_id,
      comment: '',
      is_selected: 0,
    });
  },
  [mutationTypes.removeStimulationCombination](state, { id }) {
    state.programTypesStimulationCombinations =
      state.programTypesStimulationCombinations.filter((c) => c.id !== id);
    state.programTypesStimulationCombinationsProducts =
      state.programTypesStimulationCombinationsProducts.filter(
        (p) => p.combination_id !== id
      );
  },
  [mutationTypes.updateStimulationCombinationComment](state, { id, comment }) {
    const combo = state.programTypesStimulationCombinations.find(
      (c) => c.id === id
    );
    if (combo) combo.comment = comment;
  },
  [mutationTypes.addStimulationCombinationProduct](
    state,
    { combination_id, product_id }
  ) {
    state.programTypesStimulationCombinationsProducts.push({
      id: nanoid(),
      combination_id,
      product_id,
      quantity: null,
      current_quantity: null,
    });
  },
  [mutationTypes.removeStimulationCombinationProduct](state, { id }) {
    state.programTypesStimulationCombinationsProducts =
      state.programTypesStimulationCombinationsProducts.filter(
        (p) => p.id !== id
      );
  },
  [mutationTypes.setStimulationCombinationProductQuantity](state, payload) {
    const prod = state.programTypesStimulationCombinationsProducts.find(
      (p) => p.id === payload.id
    );
    if (!prod) return;
    if (Object.prototype.hasOwnProperty.call(payload, 'current_quantity')) {
      prod.current_quantity = payload.current_quantity;
      return;
    }
    prod.quantity = payload.quantity;
  },
  [mutationTypes.setSelectedStimulationCombination](state, { id }) {
    // Delegate to the unified handler
    mutations[mutationTypes.setSelectedCombination](state, {
      id,
      forStimulationType: true,
    });
  },

  [mutationTypes.saveProgramStart](state) {
    state.isSaving = true;
    state.error = null;
  },
  [mutationTypes.saveProgramFailure](state, payload) {
    state.isSaving = false;
    state.error = payload;
  },
  [mutationTypes.saveProgramSuccess](state, payload) {
    state.isSaving = false;
    state.error = null;
    state.savedRedirectId = payload;
    state.serverData = cloneDeep(
      Object.fromEntries(
        ['program', ...editableKeysProgram].map((key) => [key, state[key]])
      )
    );
  },
};

const actions = {
  async [actionTypes.fetchProgram]({ commit, rootState }, payload) {
    commit(mutationTypes.programFetchingStart);
    try {
      const timestampGeobase = rootState.glossaryGeobase.timestamp;
      const timestampClients = rootState.clientsList.timestampClients;
      const res = await (payload
        ? api.getEditProgramData(
            payload,
            state.timestamp,
            timestampGeobase,
            timestampClients
          )
        : api.getNewProgramData(
            state.timestamp,
            timestampGeobase,
            timestampClients
          ));

      if (res.status === 'ok') {
        if (res.programPhases) {
          res.programPhases.sort((a, b) => a.order - b.order);
        }

        commit(mutationTypes.programFetchingSuccess, deepFreeze(res));
        if (res.geoBase) {
          commit(
            geobaseMutationTypes.getGeobaseSuccess,
            deepFreeze(res.geoBase),
            {
              root: true,
            }
          );
        }

        if (res.clients) {
          commit(clientsMutaionTypes.setClients, res.clients, { root: true });
        }

        if (res.products?.length) {
          commit(plantsCareMutationTypes.setProducts, res, {
            root: true,
          });
        }
      } else {
        throw Error(res.message || 'Unknown error');
      }
    } catch (err) {
      commit(mutationTypes.programFetchingFailure, err);
    }
  },
  async [actionTypes.fetchClient]({ commit }, payload) {
    commit(mutationTypes.clientFetchingStart);
    try {
      const { controller, promise } = api.getClientData(payload);
      commit(mutationTypes.clientFetchingSetAbortController, controller);
      const res = await promise;

      if (res.status === 'ok') {
        if (res.client === null) throw Error('Client not found');
        commit(mutationTypes.clientFetchingSuccess, deepFreeze(res));
      } else {
        throw Error(res.message || 'Unknown error');
      }
    } catch (err) {
      commit(mutationTypes.clientFetchingFailure, err);
    }
  },
  async [actionTypes.templatesSearch]({ commit }, payload) {
    commit(mutationTypes.templatesSearchStart);
    try {
      const { controller, promise } = api.findTemplates(payload);
      commit(mutationTypes.templatesSearchSetAbortController, controller);
      const res = await promise;
      if (res.status === 'ok') {
        commit(
          mutationTypes.templatesSearchSuccess,
          Object.freeze(res.templates ?? [])
        );
      } else throw Error(res.message || 'Unknown error');
    } catch (err) {
      commit(mutationTypes.templatesSearchFailure, err);
    }
  },
  async [actionTypes.fetchTemplate]({ commit, rootState }, payload) {
    commit(mutationTypes.templateFetchingStart);
    try {
      const res = await api.getEditTemplateData(
        payload,
        0,
        Number(new Date()),
        rootState.plantsCare.productsTimestamp
      );
      if (res.status === 'ok') {
        commit(mutationTypes.templateFetchingSuccess, deepFreeze(res));
        if (res.products?.length) {
          commit(plantsCareMutationTypes.setProducts, res, {
            root: true,
          });
        }
      } else throw Error(res.message || 'Unknown error');
    } catch (err) {
      commit(mutationTypes.templateFetchingFailure, err);
    }
  },
  async [actionTypes.saveProgram]({ commit, state }) {
    commit(mutationTypes.saveProgramStart);
    try {
      const payload = Object.fromEntries(
        ['program', ...editableKeysProgram].map((key) => [key, state[key]])
      );
      payload.programPhases = payload.programPhases.map((p, i) => ({
        ...p,
        order: i + 1,
      }));

      payload.programPhasesPests = state.programPhasesPests ?? [];
      payload.programPhasesTypesStimulation =
        state.programPhasesTypesStimulation ?? [];

      // Include combo arrays in save payload (already in editableKeysProgram)

      const res = await api.saveProgramData(payload);

      if (res.status === 'ok') {
        commit(mutationTypes.saveProgramSuccess, res.id);
      } else throw Error(res.message || 'Unknown error');
    } catch (err) {
      commit(mutationTypes.saveProgramFailure, err);
    }
  },
  async [actionTypes.deleteProgram]({ commit, state }) {
    commit(mutationTypes.saveProgramStart);
    try {
      const res = await api.saveProgramData({
        delete: true,
        program: { id: state.program.id },
      });
      if (res.status !== 'ok') throw Error(res.message || 'Unknown error');
      commit(mutationTypes.saveProgramSuccess, {});
    } catch (err) {
      commit(mutationTypes.saveProgramFailure, err);
    }
  },
};

export { mutationTypes, actionTypes, serverKeysProgram };

export default {
  state,
  actions,
  mutations,
  getters,
};
